// A player's slime and player code. Shared by the site and the game server, so keep it free of DOM and three.js.

export interface SlimeLook {
  shape: string;
  colour: string;
  eyes: string;
  mouth: string;
  cheeks: boolean;
  top: string;
  inside: string;
  finish: string; // 'clear' jelly shows the insides; 'solid' is opaque (and will take patterned skins later)
  rod: string; // the star rod it holds in Meteor Defense; its top is also the shape of its shots
}

export type PartKey = Exclude<keyof SlimeLook, 'cheeks'>;

export interface Part {
  key: PartKey;
  label: string;
  ja: string;
  values: string[];
  names: string[];
  namesJa: string[]; // the same names in Japanese, for a device set to Japanese
}

// The free parts every student can pick in the creator. Add values, never rename one: saved slimes keep them.
export const PARTS: Part[] = [
  { key: 'shape', label: 'Shape', ja: '形', values: ['mochi', 'drop', 'round', 'bean'], names: ['Mochi', 'Drop', 'Round', 'Bean'], namesJa: ['もち', 'しずく', 'まる', 'まめ'] },
  {
    key: 'colour',
    label: 'Color',
    ja: '色',
    values: ['matcha', 'sakura', 'yuzu', 'soda', 'cream'],
    names: ['Matcha', 'Sakura', 'Yuzu', 'Ramune', 'Mochi white'],
    namesJa: ['抹茶', 'さくら', 'ゆず', 'ラムネ', 'もち白'],
  },
  { key: 'eyes', label: 'Eyes', ja: '目', values: ['dots', 'tiny', 'smiley'], names: ['Dots', 'Tiny', 'Smiley'], namesJa: ['てん', 'ちいさめ', 'にっこり'] },
  { key: 'mouth', label: 'Mouth', ja: '口', values: ['smile', 'open', 'o', 'none'], names: ['Smile', 'Open', 'Oh', 'None'], namesJa: ['にこっ', 'あーん', 'おっ', 'なし'] },
  { key: 'top', label: 'On top', ja: '頭', values: ['none', 'sprout', 'bow'], names: ['Nothing', 'Sprout', 'Bow'], namesJa: ['なし', 'ふたば', 'リボン'] },
  { key: 'inside', label: 'Inside', ja: '中身', values: ['none', 'bubbles'], names: ['Nothing', 'Bubbles'], namesJa: ['なし', 'あわ'] },
  { key: 'finish', label: 'Jelly', ja: '質感', values: ['clear', 'solid'], names: ['Clear', 'Solid'], namesJa: ['すきとおる', 'つやつや'] },
  { key: 'rod', label: 'Star rod', ja: 'ステッキ', values: ['star'], names: ['Star'], namesJa: ['星'] },
];

// Parts the renderer can draw that students earn in games (see the rewards roadmap). A student may wear one
// only once its id (`colour:rust`) is in their owned list; the game server is the only one that adds to it.
// `ja` is the Japanese name on its tile; `jaFull` says what it is where it stands alone (a capsule's prize), if different.
export const EARNABLE: Partial<Record<PartKey, { value: string; name: string; ja: string; jaFull?: string }[]>> = {
  colour: [
    { value: 'rust', name: 'Persimmon', ja: '柿色' },
    { value: 'plum', name: 'Azuki', ja: 'あずき色' },
    { value: 'indigo', name: 'Indigo', ja: '藍色' },
  ],
  eyes: [
    { value: 'sparkle', name: 'Sparkly', ja: 'キラキラ', jaFull: 'キラキラの目' },
    { value: 'sleepy', name: 'Sleepy', ja: 'ねむそう', jaFull: 'ねむそうな目' },
    { value: 'drowsy', name: 'Drowsy', ja: 'とろん', jaFull: 'とろんとした目' },
  ],
  mouth: [{ value: 'cat', name: 'Cat', ja: 'ねこ', jaFull: 'ねこの口' }],
  top: [
    { value: 'sakura', name: 'Sakura', ja: 'さくら', jaFull: 'さくらの花' },
    { value: 'beret', name: 'Beret', ja: 'ベレー帽' },
    { value: 'hachimaki', name: 'Headband', ja: 'はちまき' },
    { value: 'glasses', name: 'Glasses', ja: 'めがね' },
    { value: 'pumpkin', name: 'Jack-o’-lantern', ja: 'ジャック・オ・ランタン' }, // Halloween's limited gift (see seasons.ts), not in the capsules
    { value: 'hissho', name: 'Exam headband', ja: '必勝はちまき' }, // the exam season's (seasons.ts): brought to 高3 slimes' rooms by the Inari's foxes
  ],
  inside: [
    { value: 'stars', name: 'Stars', ja: '星', jaFull: '星の中身' },
    { value: 'hearts', name: 'Hearts', ja: 'ハート', jaFull: 'ハートの中身' },
    { value: 'petals', name: 'Petals', ja: '花びら', jaFull: '花びらの中身' },
    { value: 'sparkles', name: 'Sparkles', ja: 'キラキラ', jaFull: 'キラキラの中身' },
  ],
  finish: [{ value: 'ghost', name: 'Ghost', ja: 'おばけ', jaFull: 'おばけゼリー' }], // Halloween's rare capsule part (see seasons.ts): pale, see-through and floating
  // star rods: konpeito and comet are always in the machine; moon, heart and sakura only in their seasons (seasons.ts)
  rod: [
    { value: 'heart', name: 'Heart', ja: 'ハート', jaFull: 'ハートのステッキ' },
    { value: 'konpeito', name: 'Konpeito', ja: '金平糖', jaFull: '金平糖のステッキ' },
    { value: 'moon', name: 'Crescent moon', ja: '三日月', jaFull: '三日月ステッキ' },
    { value: 'sakura', name: 'Sakura', ja: '桜', jaFull: '桜のステッキ' },
    { value: 'comet', name: 'Comet', ja: 'ほうき星', jaFull: 'ほうき星のステッキ' },
  ],
};

export const itemId = (key: PartKey, value: string) => `${key}:${value}`;

// Each star rod's Japanese name and its little line, shown when it comes out of a capsule and in the creator.
export const RODS: Record<string, { ja: string; line: string; lineJa: string }> = {
  star: { ja: '星のステッキ', line: "Every slime's first rod.", lineJa: 'みんなの最初のステッキ。' },
  heart: { ja: 'ハートのステッキ', line: 'Hits with feeling.', lineJa: '気持ちをこめて、えいっ！' },
  konpeito: { ja: '金平糖のステッキ', line: 'Throws star candy.', lineJa: '金平糖をなげちゃう。' },
  moon: { ja: '三日月ステッキ', line: 'Meteors will be punished!', lineJa: 'メテオにおしおきよ！' },
  sakura: { ja: '桜のステッキ', line: 'Spring, any time of year.', lineJa: 'いつでも春。' },
  comet: { ja: 'ほうき星のステッキ', line: 'A shooting star to stop falling stars. Make a wish.', lineJa: '流れ星で流れ星を止める。願いごとをどうぞ。' },
};

// An item's line in English and Japanese, for the ones that have one (the star rods), e.g. "rod:moon"
export function itemLine(id: string): { en: string; ja: string } | null {
  const [key, value] = id.split(':');
  const r = key === 'rod' ? RODS[value] : undefined;
  return r ? { en: r.line, ja: r.lineJa } : null;
}

// ---------- capsule machine ----------
// One capsule token = one turn. The odds are shown to students: a tier is drawn first, then a part inside it.
// A part the slime already owns comes out as konpeito instead, and konpeito can buy more tokens.

export type Tier = 'common' | 'rare' | 'special';

export const CAPSULE_TIERS: { tier: Tier; label: string; ja: string; chance: number; items: string[] }[] = [
  { tier: 'common', label: 'Common', ja: 'ふつう', chance: 70, items: ['colour:rust', 'colour:plum', 'colour:indigo', 'mouth:cat', 'eyes:sleepy', 'eyes:drowsy', 'top:hachimaki', 'rod:konpeito'] },
  { tier: 'rare', label: 'Rare', ja: 'レア', chance: 25, items: ['eyes:sparkle', 'top:sakura', 'top:beret', 'top:glasses', 'inside:hearts', 'inside:petals'] },
  { tier: 'special', label: 'Special', ja: 'とくべつ', chance: 5, items: ['inside:stars', 'inside:sparkles', 'rod:comet'] },
];

export const KONPEITO_PER_TOKEN = 30; // swap konpeito for one more turn
export const DUPLICATE_KONPEITO = 10; // what a part you already own turns into

// A season's rare parts in the machine (seasons.ts capsulesOn): their chance comes out of Common, and they count as
// Special, in the season's own capsule (`look`: its two halves' colors, and a chip's text color).
type CapsuleColors = { top: string; bottom: string; text: string };
export type SeasonCapsules = { season: { name: string; ja: string; icon: string }; items: string[]; chance: number; look?: CapsuleColors }[];
export type CapsuleOdds = { tier: Tier; label: string; ja: string; chance: number; items: string[]; icon?: string; look?: CapsuleColors };

// The machine's odds today, as the students see them. Shared so the server and the odds list agree.
export function capsuleOdds(extra: SeasonCapsules = []): CapsuleOdds[] {
  const taken = extra.reduce((n, e) => n + e.chance, 0);
  return [
    ...extra.map((e) => ({ tier: 'special' as Tier, label: e.season.name, ja: e.season.ja, chance: e.chance, items: e.items, icon: e.season.icon, look: e.look })),
    ...CAPSULE_TIERS.map((t) => (t.tier === 'common' ? { ...t, chance: t.chance - taken } : t)),
  ];
}

// Draws one capsule with two random numbers in [0, 1).
export function drawCapsule(r1: number, r2: number, extra: SeasonCapsules = []): { item: string; tier: Tier } {
  const odds = capsuleOdds(extra);
  let at = r1 * 100;
  const t = odds.find((x) => (at -= x.chance) < 0) ?? odds[odds.length - 1];
  return { item: t.items[Math.min(t.items.length - 1, Math.floor(r2 * t.items.length))], tier: t.tier };
}

// A name for an item id that says what it is, e.g. "colour:rust" -> "Persimmon color", "mouth:cat" -> "Cat mouth"
// (in Japanese 柿色, ねこの口).
const KINDS: Partial<Record<PartKey, string>> = { colour: 'color', eyes: 'eyes', mouth: 'mouth', inside: 'inside', finish: 'jelly', rod: 'rod' };
export function itemName(id: string, lang: 'en' | 'ja' = 'en'): string {
  const [key, value] = id.split(':') as [PartKey, string];
  const x = EARNABLE[key]?.find((e) => e.value === value);
  if (!x) return id;
  if (lang === 'ja') return x.jaFull ?? x.ja;
  return KINDS[key] ? `${x.name} ${KINDS[key]}` : x.name;
}

// A tier's items as short phrases, with parts of one kind run together: "Persimmon, azuki, or indigo color"
// (in Japanese, each prize on its own: 柿色、あずき色、藍色).
export function itemList(ids: string[], lang: 'en' | 'ja' = 'en'): string[] {
  if (lang === 'ja') return [ids.map((id) => itemName(id, 'ja')).join('、')];
  const groups = new Map<string, string[]>();
  for (const id of ids) {
    const key = id.split(':')[0];
    groups.set(key, [...(groups.get(key) ?? []), id]);
  }
  return [...groups].flatMap(([key, group]) => {
    const kind = KINDS[key as PartKey];
    if (!kind || group.length < 2) return group.map((id) => itemName(id));
    const names = group.map((id, n) => {
      const name = EARNABLE[key as PartKey]?.find((x) => x.value === id.split(':')[1])?.name ?? id;
      return n ? name.toLowerCase() : name;
    });
    const last = names.pop();
    return [`${names.join(', ')}${names.length > 1 ? ',' : ''} or ${last} ${kind}`];
  });
}

// Owned item ids, cleaned the same way on both sides: only earnable parts, no repeats.
export function cleanOwned(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const known = new Set(Object.entries(EARNABLE).flatMap(([k, xs]) => xs!.map((x) => itemId(k as PartKey, x.value))));
  return [...new Set(v.filter((x): x is string => typeof x === 'string' && known.has(x)))];
}

export const DEFAULT_LOOK: SlimeLook = {
  shape: 'mochi',
  colour: 'matcha',
  eyes: 'dots',
  mouth: 'smile',
  cheeks: true,
  top: 'sprout',
  inside: 'bubbles',
  finish: 'clear',
  rod: 'star',
};

// Anything unknown, or earnable but not owned, falls back to the default part, so an old or tampered look still draws.
export function cleanLook(v: unknown, owned: readonly string[] = []): SlimeLook {
  const r = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  const out = { ...DEFAULT_LOOK };
  for (const p of PARTS) {
    const x = r[p.key];
    if (typeof x !== 'string') continue;
    const earned = EARNABLE[p.key]?.some((e) => e.value === x) && owned.includes(itemId(p.key, x));
    if (p.values.includes(x) || earned) out[p.key] = x;
  }
  if (typeof r.cheeks === 'boolean') out.cheeks = r.cheeks;
  return out;
}

export function randomLook(rand: () => number = Math.random): SlimeLook {
  const pick = (a: string[]) => a[Math.floor(rand() * a.length)];
  const look = { ...DEFAULT_LOOK, cheeks: rand() < 0.7 };
  for (const p of PARTS) look[p.key] = pick(p.values);
  return look;
}

// ---------- the teacher's slime ----------
// Only the teacher's slime wears these, and only the teacher pass can save them. Students' looks go through
// cleanLook, which never keeps them.

export interface TeacherExtras {
  beard: 'none' | 'dark' | 'white'; // dark is Spencer's own; white is the sage's
  shawl: boolean; // a floating hagoromo over the shoulders
  halo: boolean;
}
export type TeacherLook = SlimeLook & TeacherExtras;
export interface TeacherSlime {
  name: string;
  look: TeacherLook;
}

export const DEFAULT_TEACHER: TeacherSlime = {
  name: '先生様',
  look: { ...DEFAULT_LOOK, colour: 'cream', top: 'none', inside: 'sparkles', beard: 'dark', shawl: true, halo: true },
};

// The teacher owns every part, earnable or not.
export const ALL_ITEMS = Object.entries(EARNABLE).flatMap(([k, xs]) => xs!.map((x) => itemId(k as PartKey, x.value)));

export function cleanTeacher(v: unknown): TeacherSlime {
  const r = (v && typeof v === 'object' ? v : {}) as { name?: unknown; look?: Record<string, unknown> };
  const l = r.look && typeof r.look === 'object' ? r.look : {};
  const name =
    typeof r.name === 'string'
      ? r.name.normalize('NFKC').replace(/[\p{C}]/gu, '').trim().slice(0, 12)
      : '';
  return {
    name: name || DEFAULT_TEACHER.name,
    look: {
      ...cleanLook(l, ALL_ITEMS),
      beard: l.beard === 'dark' || l.beard === 'white' || l.beard === 'none' ? l.beard : DEFAULT_TEACHER.look.beard,
      shawl: typeof l.shawl === 'boolean' ? l.shawl : DEFAULT_TEACHER.look.shawl,
      halo: typeof l.halo === 'boolean' ? l.halo : DEFAULT_TEACHER.look.halo,
    },
  };
}

// ---------- player codes ----------
// A code is a two-word name from these lists plus four digits, like "Brave Otter 4821". The name shows in games;
// the digits keep it this student's own. Easy English words, nothing that could be read as unkind.

export const ADJECTIVES = [
  'Brave', 'Sleepy', 'Lucky', 'Quiet', 'Swift', 'Cosmic', 'Fuzzy', 'Golden',
  'Mighty', 'Tiny', 'Jolly', 'Clever', 'Sunny', 'Misty', 'Bouncy', 'Gentle',
  'Happy', 'Calm', 'Bright', 'Cozy', 'Silver', 'Wild', 'Shy', 'Kind',
  'Proud', 'Snowy', 'Rainy', 'Merry', 'Noble', 'Wise', 'Breezy', 'Starry',
];

export const NOUNS = [
  'Otter', 'Comet', 'Panda', 'Maple', 'Falcon', 'Mochi', 'Tiger', 'Pebble',
  'Robot', 'Koala', 'Rocket', 'Lantern', 'Acorn', 'Dumpling', 'Penguin', 'Cloud',
  'Fox', 'Owl', 'Rabbit', 'Turtle', 'Dango', 'Kite', 'Island', 'River',
  'Planet', 'Melon', 'Peach', 'Cookie', 'Puffin', 'Hedgehog', 'Dolphin', 'Sparrow',
];

export function randomName(rand: () => number = Math.random): string {
  return `${ADJECTIVES[Math.floor(rand() * ADJECTIVES.length)]} ${NOUNS[Math.floor(rand() * NOUNS.length)]}`;
}

export function validName(name: string): boolean {
  const [a, n, ...rest] = name.split(' ');
  return !rest.length && ADJECTIVES.includes(a) && NOUNS.includes(n);
}

// Codes are typed on school keyboards: ignore case, spaces and anything that isn't a letter or digit.
export function normaliseCode(code: string): string {
  return code.normalize('NFKC').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function codeLooksRight(code: string): boolean {
  return /^[a-z]{4,24}\d{4}$/.test(normaliseCode(code));
}
